const API_URL = process.env.REACT_APP_BACKEND_URL;

import { compressImageInBrowser } from '../utils/compressImage';
import { writeCache, readCache, CACHE_KEYS } from '../utils/offlineCache';

/**
 * Cache-aware GET wrapper. Returns the live response on success and
 * mirrors it into IndexedDB. If the live call fails (network down,
 * timeout, 502/503), falls back to whatever was last cached at
 * `cacheKey`. The fallback is tagged with `__from_cache: true` so
 * callers can show a "stale" indicator if they want.
 */
async function getWithCache(url, cacheKey, { headers = getHeaders() } = {}) {
  try {
    const r = await fetch(url, { headers, credentials: 'include' });
    const data = await handleResponse(r);
    if (cacheKey) writeCache(cacheKey, data); // fire-and-forget
    return data;
  } catch (err) {
    // Network / server failure. Try cache.
    if (cacheKey) {
      const cached = await readCache(cacheKey);
      if (cached?.data) {
        // Annotate but don't mutate arrays in place. Wrap arrays so the
        // marker is visible without changing iteration semantics.
        const payload = cached.data;
        try {
          if (Array.isArray(payload)) {
            payload.__from_cache = true;
            payload.__cached_at = cached.cached_at;
          } else if (payload && typeof payload === 'object') {
            payload.__from_cache = true;
            payload.__cached_at = cached.cached_at;
          }
        } catch (_) { /* ignore non-extensible objects */ }
        return payload;
      }
    }
    throw err;
  }
}

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    // Global 401 handler — if the token is truly invalid, the server
    // returns 401 with detail like "Invalid token" or "Not authenticated".
    // Surface a clean "Session expired" error and broadcast a
    // window event so AuthContext can route the user to /login without
    // every individual page needing its own handler.
    if (response.status === 401) {
      let detail = 'Session expired. Please log in again.';
      try {
        const j = await response.clone().json();
        detail = j?.detail || detail;
      } catch (_) { /* ignore */ }
      // Only force a logout if the user actually HAD a token (i.e. they
      // think they're logged in). Anonymous 401s on /me are normal and
      // shouldn't trigger the logout event.
      if (localStorage.getItem('token')) {
        try {
          window.dispatchEvent(new CustomEvent('tsp:session-expired', {
            detail: { reason: detail },
          }));
        } catch (_) { /* ignore */ }
      }
      throw new Error(detail);
    }
    let errorMessage = 'Request failed';
    try {
      const error = await response.json();
      errorMessage = error.detail || error.message || errorMessage;
    } catch (e) {
      // JSON parsing failed, try text
      try {
        errorMessage = await response.text() || errorMessage;
      } catch (e2) {
        // Ignore
      }
    }
    throw new Error(errorMessage);
  }
  return response.json();
};

// Dashboard
export const getDashboardStats = () =>
  getWithCache(`${API_URL}/api/dashboard/stats`, CACHE_KEYS.dashboardStats);

export const getRecentActivity = (limit = 20) => 
  fetch(`${API_URL}/api/dashboard/recent-activity?limit=${limit}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const getStockMovementSummary = (days = 7) => 
  fetch(`${API_URL}/api/dashboard/stock-movement-summary?days=${days}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

// Products
export const getProducts = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.category_id) queryParams.append('category_id', params.category_id);
  if (params.location_id) queryParams.append('location_id', params.location_id);
  if (params.search) queryParams.append('search', params.search);
  if (params.low_stock) queryParams.append('low_stock', 'true');
  if (params.fields) queryParams.append('fields', params.fields);

  const url = `${API_URL}/api/products?${queryParams}`;
  // Only the unfiltered call seeds the offline cache — filtered
  // variants (by location / category) are too many to mirror safely
  // and the user's primary need offline is "show me all products".
  // We also cache the `fields=summary` variant separately under its
  // own key so it doesn't clobber the full-doc cache used by detail
  // views, and vice-versa.
  const isUnfiltered = !params.category_id && !params.location_id
                       && !params.search && !params.low_stock;
  let cacheKey = null;
  if (isUnfiltered) {
    cacheKey = params.fields === 'summary'
      ? `${CACHE_KEYS.products}__summary`
      : CACHE_KEYS.products;
  }
  return getWithCache(url, cacheKey);
};

// Paginated products — used by ProductsPage to render only one page at a
// time (default 50 items). Returns { items, total, page, page_size, total_pages }.
export const getProductsPaginated = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.category_id) queryParams.append('category_id', params.category_id);
  if (params.location_id) queryParams.append('location_id', params.location_id);
  if (params.search) queryParams.append('search', params.search);
  if (params.low_stock) queryParams.append('low_stock', 'true');
  queryParams.append('page', String(params.page || 1));
  queryParams.append('page_size', String(params.page_size || 50));

  return fetch(`${API_URL}/api/products/paginated?${queryParams}`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);
};

export const getProduct = (id) => 
  fetch(`${API_URL}/api/products/${id}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const getProductByBarcode = (barcode) => 
  fetch(`${API_URL}/api/products/barcode/${barcode}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const getProductBySku = (sku) => 
  fetch(`${API_URL}/api/products/sku/${sku}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const createProduct = (data) => 
  fetch(`${API_URL}/api/products`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

export const updateProduct = (id, data) => 
  fetch(`${API_URL}/api/products/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

export const deleteProduct = (id) => 
  fetch(`${API_URL}/api/products/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

// Categories
export const getCategories = () =>
  getWithCache(`${API_URL}/api/categories`, CACHE_KEYS.categories);

export const createCategory = (data) => 
  fetch(`${API_URL}/api/categories`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

export const deleteCategory = (id) => 
  fetch(`${API_URL}/api/categories/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

// Locations
export const getLocations = () =>
  getWithCache(`${API_URL}/api/locations`, CACHE_KEYS.locations);

export const createLocation = (data) => 
  fetch(`${API_URL}/api/locations`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

export const updateLocation = (id, data) => 
  fetch(`${API_URL}/api/locations/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

export const deleteLocation = (id) => 
  fetch(`${API_URL}/api/locations/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

// Move Product to New Location.
// `clientActionId` (optional) — UUID stamped on the request via the
// `X-Client-Action-Id` header so the backend can dedup a replayed
// queued move (Phase 2A offline sync). When omitted, normal online
// behaviour is preserved.
export const moveProduct = (productId, toLocationId, quantity, notes = '', serialNumbers = [], clientActionId = null) => {
  const headers = getHeaders();
  if (clientActionId) headers['X-Client-Action-Id'] = clientActionId;
  return fetch(`${API_URL}/api/products/${productId}/move`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ to_location_id: toLocationId, quantity, notes, serial_numbers: serialNumbers })
  }).then(handleResponse);
};

// Serial Numbers
export const getProductSerials = (productId) =>
  fetch(`${API_URL}/api/products/${productId}/serials`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const addProductSerials = (productId, serialNumbers) =>
  fetch(`${API_URL}/api/products/${productId}/serials`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ serial_numbers: serialNumbers })
  }).then(handleResponse);

export const removeProductSerial = (productId, serialNumber) =>
  fetch(`${API_URL}/api/products/${productId}/serials/${encodeURIComponent(serialNumber)}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const updateSerialStatus = (productId, serialNumber, status) =>
  fetch(`${API_URL}/api/products/${productId}/serials/${encodeURIComponent(serialNumber)}/status`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ status })
  }).then(handleResponse);

export const searchSerials = (query) =>
  fetch(`${API_URL}/api/serials/search?q=${encodeURIComponent(query)}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

// Suppliers
export const getSuppliers = () =>
  getWithCache(`${API_URL}/api/suppliers`, CACHE_KEYS.suppliers);

export const createSupplier = (data) => 
  fetch(`${API_URL}/api/suppliers`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

export const updateSupplier = (id, data) =>
  fetch(`${API_URL}/api/suppliers/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

export const deleteSupplier = (id) => 
  fetch(`${API_URL}/api/suppliers/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

// Stock Movements
export const getStockMovements = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.product_id) queryParams.append('product_id', params.product_id);
  if (params.movement_type) queryParams.append('movement_type', params.movement_type);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.from_location_id) queryParams.append('from_location_id', params.from_location_id);
  if (params.to_location_id) queryParams.append('to_location_id', params.to_location_id);
  if (params.search) queryParams.append('search', params.search);
  if (params.limit) queryParams.append('limit', params.limit);
  
  return fetch(`${API_URL}/api/stock-movements?${queryParams}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);
};

export const createStockMovement = (data) => 
  fetch(`${API_URL}/api/stock-movements`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

// Users (Admin)
export const getUsers = () => 
  fetch(`${API_URL}/api/users`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const updateUser = (id, data) => 
  fetch(`${API_URL}/api/users/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

// Notifications
export const getNotifications = (unreadOnly = false) => 
  fetch(`${API_URL}/api/notifications?unread_only=${unreadOnly}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const markNotificationRead = (id) => 
  fetch(`${API_URL}/api/notifications/${id}/read`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const markAllNotificationsRead = () => 
  fetch(`${API_URL}/api/notifications/read-all`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const getUnreadCount = () => 
  fetch(`${API_URL}/api/notifications/unread-count`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

// Reports
export const getInventoryReport = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.category_id) queryParams.append('category_id', params.category_id);
  if (params.location_id) queryParams.append('location_id', params.location_id);

  // Cache only the unfiltered "default" view — filtered variants are
  // user-specific drilldowns where stale cache would mislead.
  const cacheKey = (!params.category_id && !params.location_id)
                    ? CACHE_KEYS.reportInventory : null;
  return getWithCache(`${API_URL}/api/reports/inventory?${queryParams}`, cacheKey);
};

export const getLowStockReport = () =>
  getWithCache(`${API_URL}/api/reports/low-stock`, CACHE_KEYS.reportLowStock);

export const getMovementsReport = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.period) queryParams.append('period', params.period);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);

  // Cache only the unfiltered default; user-specific date drilldowns
  // bypass the cache to avoid showing stale ranges offline.
  const cacheKey = (!params.start_date && !params.end_date)
                    ? CACHE_KEYS.reportMovements : null;
  return getWithCache(`${API_URL}/api/reports/movements?${queryParams}`, cacheKey);
};

// Export URLs
export const getExportUrl = (type, format, params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) queryParams.append(key, value);
  });
  return `${API_URL}/api/export/${type}/${format}?${queryParams}`;
};

// QR Code
export const getQRCode = (productId) => 
  fetch(`${API_URL}/api/qrcode/${productId}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

// Send Low Stock Email
export const sendLowStockEmail = () => 
  fetch(`${API_URL}/api/notifications/send-low-stock-email`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

// User Activity Report
export const getUserActivityReport = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  
  return fetch(`${API_URL}/api/reports/user-activity?${queryParams}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);
};

// Move Summary Report
export const getMoveSummaryReport = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.source_location) queryParams.append('source_location', params.source_location);
  if (params.destination_location) queryParams.append('destination_location', params.destination_location);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  
  return fetch(`${API_URL}/api/reports/move-summary?${queryParams}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);
};

// Units of Measure
export const getUnits = () => 
  fetch(`${API_URL}/api/units`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const createUnit = (data) => 
  fetch(`${API_URL}/api/units`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

export const deleteUnit = (id) => 
  fetch(`${API_URL}/api/units/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

// Quick Quantity Adjustment
export const adjustProductQuantity = (productId, adjustment, reason = 'Manual adjustment', serialNumbers = []) => 
  fetch(`${API_URL}/api/products/${productId}/adjust-quantity`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ adjustment, reason, serial_numbers: serialNumbers })
  }).then(handleResponse);

// Transactions
export const getTransactions = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.product_id) queryParams.append('product_id', params.product_id);
  if (params.location_id) queryParams.append('location_id', params.location_id);
  if (params.source_location_id) queryParams.append('source_location_id', params.source_location_id);
  if (params.destination_location_id) queryParams.append('destination_location_id', params.destination_location_id);
  if (params.transaction_type) queryParams.append('transaction_type', params.transaction_type);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.search) queryParams.append('search', params.search);
  if (params.limit) queryParams.append('limit', params.limit);

  // Cache only the "default" unfiltered Transactions list (or one filtered
  // ONLY by limit) so users always have a meaningful recent-50 view
  // to fall back on when offline. Filtered drill-downs bypass cache.
  const onlyHasLimit = (
    !params.product_id && !params.location_id && !params.source_location_id
    && !params.destination_location_id && !params.transaction_type
    && !params.start_date && !params.end_date && !params.search
  );
  const cacheKey = onlyHasLimit ? CACHE_KEYS.transactions : null;
  return getWithCache(`${API_URL}/api/transactions?${queryParams}`, cacheKey);
};

// Product History
export const getProductHistory = (productId, limit = 100, options = {}) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (options.crossWarehouse) params.set('cross_warehouse', 'true');
  return fetch(`${API_URL}/api/products/${productId}/history?${params.toString()}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);
};

// Merge Products
export const mergeProducts = (sourceProductIds, targetProductId, mergeQuantities = true) => 
  fetch(`${API_URL}/api/products/merge`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      source_product_ids: sourceProductIds,
      target_product_id: targetProductId,
      merge_quantities: mergeQuantities
    })
  }).then(handleResponse);

// CSV Import/Export
export const exportProductsCSV = () => {
  const token = localStorage.getItem('token');
  window.open(`${API_URL}/api/export/products/csv`, '_blank');
};

export const downloadCSVTemplate = () => {
  window.open(`${API_URL}/api/export/products/csv/template`, '_blank');
};

export const importProductsCSV = async (file, updateExisting = false) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/api/import/products/csv?update_existing=${updateExisting}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    credentials: 'include',
    body: formData
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Import failed');
  }
  
  return response.json();
};

// Permissions
export const getUserPermissions = () => 
  fetch(`${API_URL}/api/auth/permissions`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

// App-wide settings (invoice WhatsApp number, etc.)
export const getAppSettings = () =>
  fetch(`${API_URL}/api/app-settings`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const updateAppSettings = (payload) =>
  fetch(`${API_URL}/api/app-settings`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  }).then(handleResponse);

// Audit (stock count)
export const syncAudit = (payload) =>
  fetch(`${API_URL}/api/audit/sync`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  }).then(handleResponse);

// Stock Audit Module (SA-XXXX workflow)
export const createAudit = (payload) =>
  fetch(`${API_URL}/api/audits`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  }).then(handleResponse);

export const listAudits = (params = {}) => {
  const q = new URLSearchParams();
  if (params.status) q.append('status', params.status);
  if (params.location_id) q.append('location_id', params.location_id);
  q.append('page', String(params.page || 1));
  q.append('page_size', String(params.page_size || 50));
  return fetch(`${API_URL}/api/audits?${q}`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);
};

export const getAudit = (auditId) =>
  fetch(`${API_URL}/api/audits/${auditId}`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const updateAudit = (auditId, payload) =>
  fetch(`${API_URL}/api/audits/${auditId}`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  }).then(handleResponse);

export const addAuditItem = (auditId, productId) =>
  fetch(`${API_URL}/api/audits/${auditId}/items`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ product_id: productId }),
  }).then(handleResponse);

export const patchAuditItem = (auditId, productId, body) =>
  fetch(`${API_URL}/api/audits/${auditId}/items/${productId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  }).then(handleResponse);

export const removeAuditItem = (auditId, productId) =>
  fetch(`${API_URL}/api/audits/${auditId}/items/${productId}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const deleteAudit = (auditId) =>
  fetch(`${API_URL}/api/audits/${auditId}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const finalizeAudit = (auditId) =>
  fetch(`${API_URL}/api/audits/${auditId}/finalize`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const applyAuditInventory = (auditId) =>
  fetch(`${API_URL}/api/audits/${auditId}/apply-inventory`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

// Manual Invoicing (Warehouse → Store)
export const previewInvoice = (params) => {
  const q = new URLSearchParams();
  q.append('from_date', params.from_date);
  q.append('to_date', params.to_date);
  if (params.store_location_id) q.append('store_location_id', params.store_location_id);
  if (params.from_location_id) q.append('from_location_id', params.from_location_id);
  return fetch(`${API_URL}/api/invoicing/preview?${q}`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);
};

export const generateInvoice = (payload) =>
  fetch(`${API_URL}/api/invoicing/generate`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  }).then(handleResponse);

// Ledger / persistence endpoints. These let the UI show the per-store
// invoice history, the "Last Invoiced" hint under the date picker, and
// the conflict warning before a duplicate invoice is generated.
export const listInvoices = ({ page = 1, page_size = 50, store_location_id, from_location_id } = {}) => {
  const params = new URLSearchParams({ page, page_size });
  if (store_location_id) params.set('store_location_id', store_location_id);
  if (from_location_id) params.set('from_location_id', from_location_id);
  return fetch(`${API_URL}/api/invoicing/list?${params}`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);
};

export const getLastInvoice = (storeLocationId, fromLocationId) => {
  const params = new URLSearchParams({ store_location_id: storeLocationId });
  if (fromLocationId) params.set('from_location_id', fromLocationId);
  return fetch(`${API_URL}/api/invoicing/last?${params}`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);
};

export const checkInvoiceConflicts = ({ from_date, to_date, store_location_id, from_location_id }) => {
  const params = new URLSearchParams({ from_date, to_date, store_location_id });
  if (from_location_id) params.set('from_location_id', from_location_id);
  return fetch(`${API_URL}/api/invoicing/conflicts?${params}`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);
};

export const getInvoice = (invoiceId) =>
  fetch(`${API_URL}/api/invoicing/${invoiceId}`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const modifyInvoice = (invoiceId, lines) =>
  fetch(`${API_URL}/api/invoicing/${invoiceId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ lines }),
  }).then(handleResponse);

// Admin-only. Renames the invoice_number on an existing invoice (draft
// or finalized) and cascades the new value to stock_movements +
// store_ledger_entries so search + ledger references stay consistent.
// Returns { ok, invoice_id, old_number, new_number, invoice, ... }.
export const updateInvoiceNumber = (invoiceId, newNumber) =>
  fetch(`${API_URL}/api/invoicing/${invoiceId}/number`, {
    method: 'PATCH',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ new_number: newNumber }),
  }).then(handleResponse);

// Peek at the invoice number the next auto-generation would use for a
// store. Read-only — does NOT consume the number. Returns {preview,
// prefix, seq, reused_from_deleted, holes}. Used by the New Invoice
// screen so the admin sees the number before finalizing (and can
// override it via the payload's `invoice_number` field on generate).
export const previewInvoiceNumber = (storeLocationId) =>
  fetch(
    `${API_URL}/api/invoicing/next-number?store_location_id=${encodeURIComponent(storeLocationId)}`,
    { headers: getHeaders(), credentials: 'include' },
  ).then(handleResponse);

export const finalizeInvoice = (invoiceId) =>
  fetch(`${API_URL}/api/invoicing/${invoiceId}/finalize`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const deleteInvoice = (invoiceId) =>
  fetch(`${API_URL}/api/invoicing/${invoiceId}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const getInvoicePdf = (invoiceId) =>
  fetch(`${API_URL}/api/invoicing/${invoiceId}/pdf`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const getPurchasePdf = (purchaseId) =>
  fetch(`${API_URL}/api/purchases/${purchaseId}/pdf`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

// Store Account Ledger (Debit/Credit/Balance per store)
export const getStoreLedger = (storeLocationId) =>
  fetch(`${API_URL}/api/store-ledger/${storeLocationId}`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const addStoreReceipt = (payload) =>
  fetch(`${API_URL}/api/store-ledger/receipts`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  }).then(handleResponse);

export const deleteStoreLedgerEntry = (entryId) =>
  fetch(`${API_URL}/api/store-ledger/entries/${entryId}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const updateStoreLedgerEntry = (entryId, body) =>
  fetch(`${API_URL}/api/store-ledger/entries/${entryId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  }).then(handleResponse);

export const getInvoiceDraft = (params) => {
  const q = new URLSearchParams();
  q.append('from_date', params.from_date);
  q.append('to_date', params.to_date);
  q.append('store_location_id', params.store_location_id);
  if (params.from_location_id) q.append('from_location_id', params.from_location_id);
  return fetch(`${API_URL}/api/invoicing/draft?${q}`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);
};

export const saveInvoiceDraft = (payload) =>
  fetch(`${API_URL}/api/invoicing/draft`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  }).then(handleResponse);

// Push Notifications
export const getVapidPublicKey = () =>
  fetch(`${API_URL}/api/push/vapid-public-key`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const subscribeToPush = (subscription) =>
  fetch(`${API_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(subscription)
  }).then(handleResponse);

export const unsubscribeFromPush = (endpoint) =>
  fetch(`${API_URL}/api/push/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const getPushSubscriptionStatus = () =>
  fetch(`${API_URL}/api/push/subscription-status`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const testPushNotification = () =>
  fetch(`${API_URL}/api/push/test`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const checkLowStockAndNotify = () =>
  fetch(`${API_URL}/api/push/check-low-stock`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

// Product Image Upload — pre-compresses to WebP ≤ 1200px in the browser
// before upload so mobile users on slow data don't ship 4MB phone photos
// across the wire. Backend re-validates and generates the 200x200 thumb.
export const uploadProductImage = async (productId, file) => {
  const compressed = await compressImageInBrowser(file);
  const formData = new FormData();
  formData.append('file', compressed);

  return fetch(`${API_URL}/api/products/${productId}/images`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    credentials: 'include',
    body: formData
  }).then(handleResponse);
};

export const deleteProductImage = (productId, imageId) =>
  fetch(`${API_URL}/api/products/${productId}/images/${imageId}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const setProductPrimaryImage = (productId, imageId) =>
  fetch(`${API_URL}/api/products/${productId}/images/${imageId}/set-primary`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

// Pick Lists
export const getPickLists = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.assigned_to) queryParams.append('assigned_to', params.assigned_to);
  if (params.priority) queryParams.append('priority', params.priority);
  if (params.created_by) queryParams.append('created_by', params.created_by);
  
  return fetch(`${API_URL}/api/pick-lists?${queryParams}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);
};

export const getPickList = (id) => 
  fetch(`${API_URL}/api/pick-lists/${id}`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

export const createPickList = (data) =>
  fetch(`${API_URL}/api/pick-lists`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

export const updatePickList = (id, data) =>
  fetch(`${API_URL}/api/pick-lists/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data)
  }).then(handleResponse);

export const deletePickList = (id) =>
  fetch(`${API_URL}/api/pick-lists/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const addItemsToPickList = (id, items) =>
  fetch(`${API_URL}/api/pick-lists/${id}/items`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ items })
  }).then(handleResponse);

export const removeItemsFromPickList = (id, itemIds) =>
  fetch(`${API_URL}/api/pick-lists/${id}/items`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ item_ids: itemIds })
  }).then(handleResponse);

export const updatePickListItemQty = (pickListId, itemId, requestedQty) =>
  fetch(`${API_URL}/api/pick-lists/${pickListId}/items/${itemId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ requested_qty: requestedQty })
  }).then(handleResponse);

export const startPickList = (id) =>
  fetch(`${API_URL}/api/pick-lists/${id}/start`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const pickItem = (pickListId, itemId, pickedQty, notes = null, serialNumbers = []) =>
  fetch(`${API_URL}/api/pick-lists/${pickListId}/pick`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ item_id: itemId, picked_qty: pickedQty, notes, serial_numbers: serialNumbers })
  }).then(handleResponse);

export const scanToPick = (pickListId, barcode, qty = 1) =>
  fetch(`${API_URL}/api/pick-lists/${pickListId}/scan?barcode=${encodeURIComponent(barcode)}&qty=${qty}`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const completePickList = (id) =>
  fetch(`${API_URL}/api/pick-lists/${id}/complete`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const cancelPickList = (id, reason = null) =>
  fetch(`${API_URL}/api/pick-lists/${id}/cancel?${reason ? `reason=${encodeURIComponent(reason)}` : ''}`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include'
  }).then(handleResponse);

export const getPickListStats = () =>
  fetch(`${API_URL}/api/pick-lists/stats/summary`, { headers: getHeaders(), credentials: 'include' }).then(handleResponse);

// Admin maintenance — wipes every record tagged `is_test: true` across all
// tracked collections. Pair with the `test_user / Test@123` account so QA
// data is automatically isolated and can be removed in one click.
export const wipeTestData = () =>
  fetch(`${API_URL}/api/admin/wipe-test-data`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

// Admin maintenance — re-encodes every product image to WebP at 1200px max
// and generates 200x200 thumbnails. Idempotent, may take 30-90 seconds for
// ~1000 images. Returns {ok, processed, skipped, failed, bytes_saved}.
export const optimizeProductImages = () =>
  fetch(`${API_URL}/api/admin/optimize-images`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

// Admin maintenance — downloads remote location/folder image URLs (e.g.
// emergentagent customer-assets CDN) and re-hosts them locally as 600px
// max WebPs, shrinking the per-row image cost on the locations dashboard.
// Idempotent — skips entries already on the local /api/uploads/locations/
// prefix. Returns {ok, processed, skipped, failed, bytes_saved}.
export const optimizeLocationImages = () =>
  fetch(`${API_URL}/api/admin/optimize-location-images`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

// Upload (or replace) a folder/warehouse logo. Pre-compresses in the
// browser to keep mobile uploads snappy, backend re-validates and saves
// to /api/uploads/locations/ as a ~7 KB WebP. Admin only.
export const uploadLocationImage = async (locationId, file) => {
  const compressed = await compressImageInBrowser(file, { maxDimension: 600, quality: 0.78 });
  const formData = new FormData();
  formData.append('file', compressed);
  return fetch(`${API_URL}/api/locations/${locationId}/image`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
    },
    credentials: 'include',
    body: formData,
  }).then(handleResponse);
};

export const deleteLocationImage = (locationId) =>
  fetch(`${API_URL}/api/locations/${locationId}/image`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);


// ────────────────────────────────────────────────────────────────────
// Opening balances + per-company invoice numbering (admin only)
// ────────────────────────────────────────────────────────────────────
export const setStoreOpeningBalance = (storeLocationId, body) =>
  fetch(`${API_URL}/api/store-ledger/${storeLocationId}/opening-balance`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  }).then(handleResponse);

export const getStoreOpeningBalance = (storeLocationId) =>
  fetch(`${API_URL}/api/store-ledger/${storeLocationId}/opening-balance`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const setSupplierOpeningBalance = (supplierId, body) =>
  fetch(`${API_URL}/api/supplier-ledger/${supplierId}/opening-balance`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  }).then(handleResponse);

export const getSupplierOpeningBalance = (supplierId) =>
  fetch(`${API_URL}/api/supplier-ledger/${supplierId}/opening-balance`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const listInvoiceCounters = () =>
  fetch(`${API_URL}/api/admin/invoice-counters`, {
    headers: getHeaders(),
    credentials: 'include',
  }).then(handleResponse);

export const resetInvoiceCounter = (body) =>
  fetch(`${API_URL}/api/admin/invoice-counters/reset`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  }).then(handleResponse);
